import { Component, NgZone, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { CasdoorAuthService } from 'app/core/auth/casdoor-auth.service';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { AuthInitializationService } from './core/auth/auth-initialization.service';
import { Subject, takeUntil, filter } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
    get isAuthLoading(): boolean {
        return this.auth.isAuthLoading();
    }
    set isAuthLoading(value: boolean) {
        this.auth.isAuthLoading.set(value);
    }
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _waitingForNavigation = false;

    /**
     * Constructor
     */
    constructor(
        private auth: CasdoorAuthService,
        private ngZone: NgZone,
        private router: Router,
        private authInitService: AuthInitializationService
    ) { }

    ngOnInit(): void {
        // Initialize authentication state
        const url = new URL(window.location.href)

        // Redirect web visitors from /privacy-policy to /#/privacy-policy to support hash routing
        if (!Capacitor.isNativePlatform() && url.pathname === '/privacy-policy') {
            window.location.replace('/#/privacy-policy');
            return;
        }

        this.authInitService.initialize();

        // Configure StatusBar for native platforms (iOS & Android)
        if (Capacitor.isNativePlatform()) {
            this.initializeStatusBar();
        }

        // Handle web callback from Casdoor OAuth redirect
        if (!Capacitor.isNativePlatform()) {
            if (url.pathname.startsWith('/callback')) {
                const code = url.searchParams.get('code');
                const state = url.searchParams.get('state');
                const error = url.searchParams.get('error');

                if (error) {
                    console.error('Casdoor authentication error:', {
                        error: error,
                        error_description: url.searchParams.get('error_description')
                    });
                    this.router.navigate(['/sign-in']);
                    return;
                }

                if (code && state) {
                    console.log('Processing web callback from Casdoor...');
                    this.handleWebCallback(code, state);
                }
            }
        }

        // Listen to navigation events to properly handle loading state
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((event: NavigationEnd) => {
                // If we were waiting for navigation after auth success, hide loader now
                if (this._waitingForNavigation && (event.url.includes('/dashboard') || event.url === '/sign-in')) {
                    this.isAuthLoading = false;
                    this._waitingForNavigation = false;
                    console.log('Navigation completed, hiding auth loader:', event.url);
                }

                // Handle web callback if navigating to /callback
                if (!Capacitor.isNativePlatform() && event.url.startsWith('/callback')) {
                    const urlParams = new URLSearchParams(window.location.search);
                    const code = urlParams.get('code');
                    const state = urlParams.get('state');

                    if (code && state) {
                        this.handleWebCallback(code, state);
                    }
                }
            });


        if (Capacitor.isNativePlatform()) {
            App.addListener('appUrlOpen', async (event) => {
                console.log('Deep link received:', event.url);

                // Handle Universal Links (https://app.dipitatouch.com/...)
                if (event.url.includes('app.dipitatouch.com')) {
                    const url = new URL(event.url);
                    const path = url.pathname;

                    if (path.includes('privacy-policy')) {
                        console.log('Processing privacy-policy Universal Link...');
                        this.ngZone.run(() => {
                            this.router.navigate(['/privacy-policy']);
                        });
                        return;
                    }

                    if (path.includes('welcome') || path.includes('verify-email')) {
                        console.log('Processing welcome/verify Universal Link from email...');
                        this.ngZone.run(async () => {
                            try {
                                await this.authInitService.handleAuthenticationSuccess();
                            } catch (error) {
                                console.error('Error handling email deep link:', error);
                                this.router.navigate(['/dashboard']);
                            }
                        });
                        return;
                    }
                }

                // Handle custom URL schemes (com.dipitatouch.app://...)
                if (event.url.includes('callback')) {
                    console.log('Processing Casdoor callback...');
                    try { await Browser.close(); } catch { }

                    // Extract parameters from URL for debug
                    // Handle both formats: com.dipitatouch.app://callback?code=... and com.dipitatouch.app://domain/path/callback?code=...
                    let searchParams: URLSearchParams;
                    try {
                        // Try parsing as full URL first
                        const url = new URL(event.url);
                        const hash = url.hash ? url.hash.substring(1) : '';
                        searchParams = new URLSearchParams(url.search || hash);
                    } catch (e) {
                        // If URL parsing fails, try manual parsing for custom URL schemes
                        const hashIndex = event.url.indexOf('#');
                        const queryIndex = event.url.indexOf('?');
                        let paramsString = '';
                        if (hashIndex > -1) {
                            paramsString = event.url.substring(hashIndex + 1);
                        } else if (queryIndex > -1) {
                            paramsString = event.url.substring(queryIndex + 1);
                        }
                        searchParams = new URLSearchParams(paramsString);
                    }

                    const code = searchParams.get('code');
                    const state = searchParams.get('state');

                    console.log('Callback URL params:', {
                        code,
                        state,
                        error: searchParams.get('error'),
                        error_description: searchParams.get('error_description')
                    });

                    this.ngZone.run(() => {
                        // Treat manually callback URL
                        if (searchParams.get('error')) {
                            console.error('Casdoor error in callback:', {
                                error: searchParams.get('error'),
                                description: searchParams.get('error_description')
                            });
                            this.router.navigate(['/sign-in']);
                            return;
                        }

                        if (!code || !state) {
                            console.error('Casdoor callback missing code or state');
                            this.isAuthLoading = false;
                            this._waitingForNavigation = false;
                            this.router.navigate(['/sign-in']);
                            return;
                        }

                        // Set loading state immediately upon starting callback handling
                        this.isAuthLoading = true;
                        this._waitingForNavigation = true;

                        this.auth.handleRedirectCallback(code, state).subscribe({
                            next: async (result) => {
                                console.log('Auth callback success:', result);

                                try {
                                    await this.authInitService.handleAuthenticationSuccess();
                                } catch (error) {
                                    console.error('Error in auth success handler:', error);
                                    this.router.navigate(['/dashboard'], { replaceUrl: true });
                                } finally {
                                    this.isAuthLoading = false;
                                    this._waitingForNavigation = false;
                                }
                            },
                            error: (error) => {
                                console.error('Auth callback error details:', {
                                    message: error.message,
                                    error: error,
                                    url: event.url,
                                    stack: error.stack
                                });
                                this.isAuthLoading = false;
                                this._waitingForNavigation = false;
                                // Redirect to login page if error occured
                                this.router.navigate(['/sign-in']);
                            }
                        });
                    });
                } else if (event.url.includes('sign-in')) {
                    console.log('Processing logout redirect to sign-in...');
                    try { await Browser.close(); } catch { }
                    this.ngZone.run(() => {
                        this.router.navigate(['/sign-in']);
                    });
                } else if (event.url.includes('welcome') || event.url.includes('verify-email')) {
                    console.log('Processing welcome/verify deep link from email...');
                    this.ngZone.run(async () => {
                        // Redirect user to their dashboard based on role
                        try {
                            await this.authInitService.handleAuthenticationSuccess();
                        } catch (error) {
                            console.error('Error handling email deep link:', error);
                            this.router.navigate(['/dashboard']);
                        }
                    });
                } else {
                    console.log('ℹ️ Deep link not a callback URL, ignoring');
                }
            });
        }
    }

    /**
     * Initialize StatusBar configuration for mobile devices
     * Sets the status bar to be transparent with overlay content
     */
    private async initializeStatusBar(): Promise<void> {
        try {
            // Set status bar style based on platform and theme
            // Use light content (white icons) for better visibility
            await StatusBar.setStyle({ style: Style.Light });

            // Make status bar background transparent/overlay
            // This allows content to display under the status bar
            await StatusBar.setOverlaysWebView({ overlay: true });

            console.log('✅ Status Bar initialized successfully');
        } catch (error) {
            console.warn('⚠️ StatusBar plugin error (may not be available on this platform):', error);
        }
    }

    /**
     * Handle web callback from Casdoor OAuth redirect
     */
    private handleWebCallback(code: string, state: string): void {
        this.isAuthLoading = true;

        this.auth.handleRedirectCallback(code, state).subscribe({
            next: async (result) => {
                console.log('Web callback success:', result);

                try {
                    await this.authInitService.handleAuthenticationSuccess();
                } catch (error) {
                    console.error('Error in auth success handler:', error);
                    this.router.navigate(['/dashboard']);
                }

                this.isAuthLoading = false;
            },
            error: (error) => {
                console.error('Web callback error:', error);
                this.isAuthLoading = false;
                this.router.navigate(['/sign-in']);
            }
        });
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

}
